/**
 * UserTable Component
 * Tableau des utilisateurs avec actions (voir, modifier, supprimer, etc.)
 */

import { useState } from 'react';
import { UserRoleBadge } from './UserRoleBadge';
import { UserStatusBadge } from './UserStatusBadge';
import type { UserAdminDTO, UserRole } from '../../application/dto/dtos';
import {
  Eye,
  Edit,
  Trash2,
  Lock,
  Unlock,
  UserCheck,
  UserX,
  Shield,
} from 'lucide-react';

export type User = UserAdminDTO;

interface UserTableProps {
  users: UserAdminDTO[];
  loading?: boolean;
  onView: (userId: string) => void;
  onEdit: (userId: string) => void;
  onDelete: (userId: string) => void;
  onToggleActive: (userId: string, activate: boolean) => void;
  onChangeRole: (userId: string) => void;
  onLock: (userId: string) => void;
  onUnlock: (userId: string) => void;
}

export function UserTable({
  users,
  loading = false,
  onView,
  onEdit,
  onDelete,
  onToggleActive,
  onChangeRole,
  onLock,
  onUnlock,
}: UserTableProps) {
  const [openMenuId, setOpenMenuId] = useState<string | null>(null);

  const formatDate = (date: Date | string | undefined) => {
    if (!date) return 'Jamais';
    return new Intl.DateTimeFormat('fr-FR', {
      dateStyle: 'short',
      timeStyle: 'short',
    }).format(new Date(date));
  };

  const isLocked = (user: UserAdminDTO) => {
    if (user.estVerrouille !== undefined) return user.estVerrouille;
    if (!user.lockedUntil) return false;
    return new Date(user.lockedUntil) > new Date();
  };

  if (loading) {
    return (
      <div className="bg-surface rounded-lg border border-border overflow-hidden">
        <div className="p-8 text-center">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-4 border-border border-t-primary"></div>
          <p className="mt-2 text-sm text-content-subtle">Chargement...</p>
        </div>
      </div>
    );
  }

  if (users.length === 0) {
    return (
      <div className="bg-surface rounded-lg border border-border p-8 text-center">
        <p className="text-content-subtle">Aucun utilisateur trouvé</p>
      </div>
    );
  }

  return (
    <div className="bg-surface rounded-lg border border-border overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-surface-raised border-b border-border">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-content-subtle uppercase tracking-wider">
                Utilisateur
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-content-subtle uppercase tracking-wider">
                Rôle
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-content-subtle uppercase tracking-wider">
                Statut
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-content-subtle uppercase tracking-wider">
                Organisation
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-content-subtle uppercase tracking-wider">
                Dernière connexion
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-content-subtle uppercase tracking-wider">
                2FA
              </th>
              <th className="px-6 py-3 text-right text-xs font-medium text-content-subtle uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {users.map((user) => (
              <tr
                key={user.idUtilisateur}
                className="hover:bg-surface-raised transition-colors"
              >
                {/* Utilisateur */}
                <td className="px-6 py-4 whitespace-nowrap">
                  <div>
                    <div className="text-sm font-medium text-content">
                      {user.prenom} {user.nom}
                    </div>
                    <div className="text-sm text-content-subtle">
                      {user.email}
                    </div>
                  </div>
                </td>

                {/* Rôle */}
                <td className="px-6 py-4 whitespace-nowrap">
                  <UserRoleBadge role={user.role as UserRole} size="sm" />
                </td>

                {/* Statut */}
                <td className="px-6 py-4 whitespace-nowrap">
                  <UserStatusBadge isActive={user.isActive} isLocked={isLocked(user)} size="sm" />
                </td>

                {/* Organisation */}
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="text-sm text-content">
                    {user.organisation || '-'}
                  </div>
                  {user.poste && (
                    <div className="text-xs text-content-subtle">
                      {user.poste}
                    </div>
                  )}
                </td>

                {/* Dernière connexion */}
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="text-sm text-content">
                    {formatDate(user.lastLogin || user.dateDerniereConnexion)}
                  </div>
                </td>

                {/* 2FA */}
                <td className="px-6 py-4 whitespace-nowrap">
                  {user.deuxFacteursActive ? (
                    <span className="inline-flex items-center gap-1 text-xs font-medium text-success">
                      <Shield className="w-3 h-3" />
                      Actif
                    </span>
                  ) : (
                    <span className="text-xs text-content-subtle">-</span>
                  )}
                </td>

                {/* Actions */}
                <td className="px-6 py-4 whitespace-nowrap text-right">
                  <div className="flex items-center justify-end gap-2">
                    <button
                      onClick={() => onView(user.idUtilisateur)}
                      className="p-1.5 text-content-muted hover:bg-surface-overlay
                               rounded transition-colors"
                      title="Voir détails"
                    >
                      <Eye className="w-4 h-4" />
                    </button>

                    <button
                      onClick={() => onEdit(user.idUtilisateur)}
                      className="p-1.5 text-accent hover:bg-accent-light
                               rounded transition-colors"
                      title="Modifier"
                    >
                      <Edit className="w-4 h-4" />
                    </button>

                    {user.isActive ? (
                      <button
                        onClick={() => onToggleActive(user.idUtilisateur, false)}
                        className="p-1.5 text-warning hover:bg-warning-light
                                 rounded transition-colors"
                        title="Désactiver"
                      >
                        <UserX className="w-4 h-4" />
                      </button>
                    ) : (
                      <button
                        onClick={() => onToggleActive(user.idUtilisateur, true)}
                        className="p-1.5 text-success hover:bg-success-light
                                 rounded transition-colors"
                        title="Activer"
                      >
                        <UserCheck className="w-4 h-4" />
                      </button>
                    )}

                    {isLocked(user) ? (
                      <button
                        onClick={() => onUnlock(user.idUtilisateur)}
                        className="p-1.5 text-success hover:bg-success-light
                                 rounded transition-colors"
                        title="Déverrouiller"
                      >
                        <Unlock className="w-4 h-4" />
                      </button>
                    ) : (
                      <button
                        onClick={() => onLock(user.idUtilisateur)}
                        className="p-1.5 text-warning hover:bg-warning-light
                                 rounded transition-colors"
                        title="Verrouiller"
                      >
                        <Lock className="w-4 h-4" />
                      </button>
                    )}

                    <button
                      onClick={() => onChangeRole(user.idUtilisateur)}
                      className="p-1.5 text-primary-text hover:bg-primary-light
                               rounded transition-colors"
                      title="Changer le rôle"
                    >
                      <Shield className="w-4 h-4" />
                    </button>

                    <button
                      onClick={() => onDelete(user.idUtilisateur)}
                      className="p-1.5 text-danger hover:bg-danger-light
                               rounded transition-colors"
                      title="Supprimer"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
